import type { IntentGuideData } from "./intentGuides";
import type { RelatedLink } from "@/components/seo/IntentGuide";

// Factual Siam Horizon project pages. No prices — pricing will be reintroduced by the owner.
const projectLinks: RelatedLink[] = [
  { label: "The Villas", to: "/villas" },
  { label: "Configurations", to: "/configurations" },
  { label: "Plots", to: "/plots" },
  { label: "Master Plan", to: "/masterplan" },
  { label: "Construction", to: "/construction" },
  { label: "Location", to: "/location" },
  { label: "Living in Hua Hin", to: "/hua-hin" },
  { label: "Construction Updates", to: "/construction-updates" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];
const rel = (path: string) => projectLinks.filter((l) => l.to !== path);

const page = (d: Omit<IntentGuideData, "related" | "priority" | "slug"> & { priority?: string }): IntentGuideData => ({
  ...d,
  slug: d.path.slice(1),
  priority: d.priority ?? "0.8",
  related: rel(d.path),
});

export const projectPages: IntentGuideData[] = [
  page({
    path: "/villas",
    priority: "0.9",
    eyebrow: "Siam Horizon · The Villas",
    h1: "Private Pool Villas in Hua Hin",
    title: "Pool Villas in Hua Hin | Design & Specifications | Siam Horizon",
    description: "Siam Horizon villas: 3 bedrooms, 4 bathrooms, private saltwater pool, 10 kW solar with 30 kWh battery storage, close to central Hua Hin.",
    intro: "Siam Horizon is a limited collection of premium private pool villas in the Khao Noi area of Hua Hin, designed for comfortable tropical living close to the city.",
    sections: [
      { heading: "Standard villa", body: "Each standard villa offers 3 bedrooms and 4 bathrooms (3 en-suite plus a guest WC), with an open-plan living room and kitchen under a high central ceiling.", bullets: ["Private saltwater swimming pool", "Approx. 265 sq.m. built area", "Approx. 257 sq.m. total usable area", "Covered patio and terrace", "Covered parking for up to 2 cars", "Private garden and outdoor dining"] },
      { heading: "Built area and usable area", body: "Two figures are used because they measure different things. Built area (approx. 265 sq.m.) is the constructed footprint of the villa. Usable area (approx. 257 sq.m.) adds up the spaces you actually live in: indoor rooms, patio and terrace, and pool." },
      { heading: "Fittings", body: "Villas are delivered turn-key.", bullets: ["Double-glazed aluminium windows", "Air conditioning", "Equipped kitchen and built-in cabinetry", "Storage spaces", "Water filtration", "Gated residential environment"] },
      { heading: "Energy", body: "Each standard Siam Horizon villa includes a 10 kW solar system combined with 30 kWh of battery storage, helping lower running costs." },
    ],
    faqs: [
      { q: "How many bedrooms do Siam Horizon villas have?", a: "The standard villa has 3 bedrooms and 4 bathrooms. Configuration C adds an en-suite bedroom for 4 bedrooms in total." },
      { q: "Does every villa have a private pool?", a: "Yes, every villa has its own private saltwater swimming pool." },
    ],
  }),
  page({
    path: "/configurations",
    eyebrow: "Siam Horizon · Configurations",
    h1: "Villa Configurations at Siam Horizon",
    title: "Villa Configurations | Siam Horizon Hua Hin",
    description: "Three Siam Horizon villa configurations in Hua Hin, with plots from 350–380 sq.m. up to around 800 sq.m. and an optional 4th bedroom.",
    intro: "Every Siam Horizon villa shares the same architecture and specifications. What changes is the land and the outdoor living around it.",
    sections: [
      { heading: "Configuration A", body: "Approx. 350–380 sq.m. of land.", bullets: ["Villa approx. 265 sq.m. built area", "3 bedrooms, 4 bathrooms", "Private saltwater pool"] },
      { heading: "Configuration B", body: "Approx. 550–650 sq.m. of land.", bullets: ["Villa approx. 265 sq.m. built area", "3 bedrooms, 4 bathrooms", "Gazebo / pool house configuration"] },
      { heading: "Configuration C", body: "Around 800 sq.m. of land.", bullets: ["Main villa approx. 265 sq.m. built area", "Additional en-suite bedroom — 4 bedrooms in total", "Gazebo / pool house"] },
      { heading: "Availability", body: "Configurations depend on which plots remain available. Contact us to check current availability." },
    ],
    faqs: [{ q: "Can two plots be combined?", a: "Yes, larger configurations are created by combining adjacent plots, subject to availability." }],
  }),
  page({
    path: "/plots",
    eyebrow: "Siam Horizon · Plots",
    h1: "Villa Plots in Khao Noi, Hua Hin",
    title: "Villa Plots in Hua Hin | From 350 to 800 sq.m. | Siam Horizon",
    description: "Plot sizes at Siam Horizon range from 350 sq.m. up to 800 sq.m., in a low-density gated community close to central Hua Hin.",
    intro: "Siam Horizon is a low-density residential project with a limited number of plots, from 350 sq.m. up to 800 sq.m.",
    sections: [
      { heading: "Plot sizes", body: "Single plots start from approx. 350 sq.m. Adjacent plots can be combined to create larger gardens, a pool house or a 4th bedroom." },
      { heading: "Ownership", body: "Villas are offered as freehold structure ownership on registered leasehold land. Our FAQ explains the options available to foreign buyers." },
    ],
    faqs: [{ q: "How many villas are in the project?", a: "Siam Horizon is a limited collection of 18 villas." }],
  }),
  page({
    path: "/masterplan",
    eyebrow: "Siam Horizon · Master Plan",
    h1: "Siam Horizon Master Plan",
    title: "Master Plan | Siam Horizon Pool Villas, Hua Hin",
    description: "The Siam Horizon master plan: a private, low-density gated community of 18 pool villas in the Khao Noi area of Hua Hin.",
    intro: "The master plan arranges a limited collection of villas around a private gated road, giving each home its own garden and pool.",
    sections: [
      { heading: "Layout", body: "Plots are laid out for privacy and natural airflow, with covered parking on each plot and low community fees." },
      { heading: "See it on the homepage", body: "The aerial view and detailed plot plan can be zoomed on the homepage master plan section." },
    ],
    faqs: [],
  }),
  page({
    path: "/construction",
    eyebrow: "Siam Horizon · Construction",
    h1: "Construction Quality: Built for Thailand, Built for the Long Term",
    title: "Construction Quality | Foundations, AAC Walls, Insulation | Siam Horizon",
    description: "How Siam Horizon villas are built: deep foundations, Q-CON AAC walls, 150 mm SCG insulation, double glazing, Mitsubishi Electric air conditioning and solar.",
    intro: "Siam Horizon villas are built for Thailand's tropical climate, with materials chosen for comfort, durability and lower running costs.",
    sections: [
      { heading: "Structure", body: "Reinforced concrete structure on foundations adapted to local site conditions, with deep footings where required." },
      { heading: "Walls and insulation", body: "Q-CON AAC wall system and approx. 150 mm roof and ceiling insulation using SCG Stay Cool products, to keep heat out." },
      { heading: "Windows, cooling and energy", bullets: ["Double-glazed aluminium windows", "Mitsubishi Electric air conditioning", "10 kW solar system with 30 kWh battery storage"], body: "" },
      { heading: "Climate-conscious design", body: "Shading, natural light and cross-ventilation are part of the architecture from the start." },
    ],
    faqs: [],
  }),
  page({
    path: "/construction-updates",
    eyebrow: "Siam Horizon · On Site",
    h1: "Siam Horizon Construction Updates",
    title: "Construction Updates | Siam Horizon Hua Hin",
    description: "Follow real construction progress at Siam Horizon in Khao Noi, Hua Hin, with photos and videos from the job site.",
    intro: "Siam Horizon is under construction. Photos and videos from the site are published on the homepage job site gallery.",
    sections: [{ heading: "Visit the site", body: "We are happy to arrange a site visit so you can see the construction in person. Contact us to book one." }],
    faqs: [],
  }),
  page({
    path: "/location",
    priority: "0.9",
    eyebrow: "Siam Horizon · Location",
    h1: "Siam Horizon Location: Khao Noi, Hua Hin",
    title: "Siam Horizon Location | Khao Noi, Hua Hin",
    description: "Discover Siam Horizon's Hua Hin location near Soi Chonprathan 44, Wat Khao Noi and Dinosaur Market, within easy reach of central Hua Hin.",
    intro: "Siam Horizon is located in the Khao Noi area of Hua Hin, close to Soi Chonprathan 44, Wat Khao Noi and Dinosaur Market. Close to Hua Hin's heart. Closer to yours.",
    sections: [
      { heading: "Close to central Hua Hin", body: "Easy access via Soi Chonprathan 44 brings beaches, markets, restaurants, hospitals and golf courses within easy reach." },
      { heading: "A quieter residential setting", body: "Close enough to enjoy Hua Hin. Far enough to enjoy your home: a gated, low-density community away from city noise." },
      { heading: "Address", body: "Prachuap Khiri Khan Province, Thailand. The map on our homepage gives exact directions." },
    ],
    faqs: [{ q: "Where exactly is Siam Horizon?", a: "In the Khao Noi area of Hua Hin, near Soi Chonprathan 44, Wat Khao Noi and Dinosaur Market." }],
  }),
  page({
    path: "/hua-hin",
    eyebrow: "Living in Hua Hin",
    h1: "Living in Hua Hin, Thailand",
    title: "Living in Hua Hin | Lifestyle, Climate & Amenities | Siam Horizon",
    description: "What life in Hua Hin offers: beaches, markets, golf, healthcare, international schools and easy access to Bangkok.",
    intro: "Hua Hin is a royal seaside town on the Gulf of Thailand, popular with Thai families and international residents alike.",
    sections: [
      { heading: "Lifestyle", bullets: ["Long sandy beaches", "Night markets and Cicada Market", "Championship golf courses", "Kaeng Krachan and Sam Roi Yot national parks"], body: "" },
      { heading: "Practical living", body: "International hospitals, schools, shopping malls and a growing range of restaurants make Hua Hin easy for year-round living." },
    ],
    faqs: [],
  }),
  page({
    path: "/faq",
    eyebrow: "Siam Horizon · FAQ",
    h1: "Frequently Asked Questions",
    title: "FAQ | Buying a Pool Villa at Siam Horizon, Hua Hin",
    description: "Answers to common questions about Siam Horizon: villas, plots, ownership for foreign buyers, energy, construction and site visits.",
    intro: "Practical answers for buyers considering a Siam Horizon pool villa.",
    sections: [],
    faqs: [
      { q: "Can foreigners buy a villa at Siam Horizon?", a: "Yes. Villas are offered as freehold structure ownership on registered leasehold land; the funds must be transferred from abroad (FET form)." },
      { q: "What energy system is included?", a: "Each standard villa includes a 10 kW solar system with 30 kWh of battery storage." },
      { q: "How many villas are there?", a: "A limited collection of 18 villas in a low-density gated community." },
      { q: "Can I visit the site?", a: "Yes, contact us to book a site visit." },
      { q: "Are maintenance services available?", a: "Yes, maintenance services are available on request." },
    ],
  }),
  page({
    path: "/contact",
    eyebrow: "Siam Horizon · Contact",
    h1: "Contact Siam Horizon",
    title: "Contact Siam Horizon | Book a Site Visit in Hua Hin",
    description: "Contact Siam Horizon to check current availability, request the brochure and floor plan, or book a site visit in Hua Hin.",
    intro: "Check current availability, request project information or book a site visit. WhatsApp: +66 63 491 4425 · Email: contact@siamhorizon.com.",
    sections: [{ heading: "Developer", body: "Sirichokthima (Thailand) Co., Ltd., 22/70 Pracha Samakhi Road, Hua Hin District, Prachuap Khiri Khan Province 77110." }],
    faqs: [],
  }),
];
